import { motion } from "framer-motion";
import { Check, Info, CalendarClock, Repeat, FileSignature } from "lucide-react";
import { Reveal } from "./Reveal";

const TYPES = ["Escritórios", "Consultórios", "Clínicas de estética", "Salas comerciais", "Imobiliárias", "Escritórios de advocacia", "Empresas de contabilidade"];

const INCLUDED = [
  "Limpeza de pisos e superfícies acessíveis",
  "Remoção de pó de móveis, mesas e balcões",
  "Limpeza de recepções, salas de atendimento e áreas administrativas",
  "Limpeza de portas e vidros acessíveis, conforme o escopo",
  "Limpeza de banheiros e reposição de itens fornecidos pelo cliente, quando contratada",
  "Limpeza de copas e pequenas áreas de convivência",
  "Retirada de lixo comum",
  "Manutenção da limpeza e organização dos ambientes compartilhados",
];

const PLANS = [
  [Repeat, "Plano de Manutenção Periódica", "Para empresas que precisam de limpeza programada uma ou mais vezes por semana, conforme a necessidade do ambiente."],
  [CalendarClock, "Plano de Atendimento Diário", "Para estabelecimentos que precisam de limpeza todos os dias úteis ou em outros dias e horários previamente combinados."],
  [FileSignature, "Contrato Mensal ou Anual", "Para empresas que desejam organizar a limpeza por meio de um contrato, com frequência, tarefas, horários e condições de pagamento definidos. O contrato anual é uma opção sujeita à negociação, não uma obrigação."],
];

const Plans = () => (
  <div id="planos-comerciais" className="mt-20 md:mt-28">
    <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
      <h3 className="font-serif-d text-4xl md:text-5xl font-medium leading-tight">Planos de atendimento comercial</h3>
      <p className="text-[#FAF8F5]/75 max-w-md text-sm md:text-base">Valores personalizados conforme o espaço, as tarefas, a frequência, os horários e os materiais necessários.</p>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-4 md:gap-5">
      {PLANS.map(([Icon, t, d], i) => (
        <motion.div key={t} data-testid={`plan-${i + 1}`}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }} whileHover={{ y: -6 }}
          className="h-full bg-[#FAF8F5]/[0.06] border border-[#FAF8F5]/15 p-7 md:p-8 flex flex-col hover:border-[#C59B27]/60 transition-colors">
          <div className="flex items-center justify-between">
            <Icon size={26} className="text-[#C59B27]" />
            <span className="font-mono-d text-xs text-[#FAF8F5]/60">0{i + 1}</span>
          </div>
          <h4 className="font-serif-d text-3xl mt-8 leading-tight">{t}</h4>
          <p className="mt-4 text-[#FAF8F5]/80 text-sm md:text-base leading-relaxed">{d}</p>
          <span className="mt-auto pt-8 font-mono-d text-[11px] tracking-[0.2em] text-[#C59B27]">SOB CONSULTA</span>
        </motion.div>
      ))}
    </div>
  </div>
);

const CtaBand = ({ onPick }) => (
  <Reveal className="mt-6 bg-[#C59B27] text-[#121518] p-8 md:p-14 grid lg:grid-cols-12 gap-8 items-center" >
    <div className="lg:col-span-7" data-testid="commercial-cta">
      <h3 className="font-serif-d text-4xl md:text-5xl font-medium leading-[1]">Sua empresa precisa de uma limpeza programada?</h3>
      <p className="mt-4 text-[#121518]/80 max-w-xl">Conheça nossas opções de atendimento e solicite uma proposta personalizada para o seu negócio.</p>
    </div>
    <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:items-stretch">
      <button data-testid="commercial-cta-orcamento" onClick={() => onPick("comercial")}
        className="rounded-full bg-[#121518] text-[#FAF8F5] px-7 py-4 text-sm font-medium hover:bg-[#0F4C3A] transition-colors">Solicitar orçamento comercial</button>
      <button data-testid="commercial-cta-visita" onClick={() => onPick("comercial", true)}
        className="rounded-full border border-[#121518] px-7 py-4 text-sm font-medium hover:bg-[#121518] hover:text-[#FAF8F5] transition-colors">Agendar uma visita técnica</button>
    </div>
  </Reveal>
);

export const Commercial = ({ onPick }) => (
  <section id="limpeza-comercial" data-testid="section-comercial" className="relative bg-[#0F4C3A] text-[#FAF8F5] py-24 md:py-36 overflow-hidden grain">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-7">
          <p className="font-mono-d text-[11px] tracking-[0.3em] text-[#C59B27] mb-6">SERVIÇO 01 · LIMPEZA COMERCIAL E CONTRATOS RECORRENTES</p>
          <h2 className="font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.95] tracking-tight">
            Limpeza Comercial para Escritórios, <em className="text-[#C59B27]">Clínicas e Empresas</em>
          </h2>
          <p className="mt-8 text-lg md:text-xl text-[#FAF8F5]/90 max-w-2xl">Seu ambiente de trabalho limpo e organizado, sem precisar manter uma equipe própria de limpeza em tempo integral.</p>
          <div className="mt-8 space-y-4 text-[#FAF8F5]/80 max-w-2xl leading-relaxed">
            <p>Oferecemos serviços de limpeza de manutenção para empresas que precisam manter seus ambientes limpos, organizados e agradáveis para funcionários, clientes e visitantes.</p>
            <p>Cada atendimento é planejado conforme o tamanho do espaço, a circulação de pessoas, as necessidades do estabelecimento e a frequência desejada — inclusive antes da abertura ou depois do encerramento do expediente, conforme disponibilidade e condições acordadas.</p>
          </div>
          <ul className="mt-8 flex flex-wrap gap-2" data-testid="commercial-types">
            {TYPES.map((t) => <li key={t} className="text-xs md:text-sm border border-[#FAF8F5]/25 rounded-full px-4 py-2">{t}</li>)}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] overflow-hidden rounded-t-full">
            <img src="/img/clinica.jpg" alt="Recepção de clínica limpa e organizada" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-4 md:-left-10 w-40 md:w-52 aspect-square overflow-hidden border-4 border-[#0F4C3A]">
            <img src="/img/mesa-luvas.jpg" alt="Limpeza de mesa de trabalho" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>

      <div className="mt-20 md:mt-28 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <h3 className="font-serif-d text-4xl md:text-5xl font-medium leading-tight">O que pode ser incluído</h3>
          <div className="mt-6 flex gap-3 text-sm text-[#FAF8F5]/80 bg-[#121518]/25 p-5" data-testid="commercial-scope-note">
            <Info size={18} className="text-[#C59B27] shrink-0 mt-0.5" />
            <p>As tarefas são definidas conforme as necessidades de cada cliente. Limpeza de equipamentos médicos, materiais contaminados, resíduos especiais, fachadas e vidros em altura não fazem parte do serviço padrão.</p>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {INCLUDED.map((it) => (
              <li key={it} className="flex gap-3 border-t border-[#FAF8F5]/15 pt-4"><Check size={18} className="text-[#C59B27] shrink-0 mt-0.5" />{it}</li>
            ))}
          </ul>
        </Reveal>
      </div>

      <Plans />
      <CtaBand onPick={onPick} />
    </div>
  </section>
);
